/**
 * Template Builder Component
 * 
 * Multi-step wizard for creating user-generated trip templates
 * Steps: Basic Info → Itinerary → Budget → Media → Preview
 */

import React, { useState, useCallback, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Platform, Alert, Pressable, ActivityIndicator, Modal } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SelectField } from '@/components/ui/SelectField';
import { SelectFieldModalProvider } from '@/components/ui/SelectFieldModalContext';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption, SubHeading } from '@/components/ui/Text';
import { Ionicons } from '@expo/vector-icons';
import { useUserStore } from '@/state/userStore';
import { createUserTemplate } from '@/services/tripTemplates';
import { adminCreateTripTemplate, adminUpdateTripTemplate } from '@/services/adminTemplates';
import { templateToBuilderDraft, type TemplateBuilderDraft } from '@/utils/templateBuilderDraft';
import { useToast } from '@/components/ui/ToastProvider';
import type {
  TripTemplate,
  TemplateDifficulty,
  TravelStyle,
  TemplateDay,
  TemplateBudget,
} from '@/types/tripTemplates';
import { MIN_TEMPLATE_ITINERARY_DAYS } from '@/types/tripTemplates';
import { TemplateBuilderBasicStep } from './TemplateBuilderBasicStep';
import { TemplateBuilderItineraryStep } from './TemplateBuilderItineraryStep';
import { TemplateBuilderBudgetStep } from './TemplateBuilderBudgetStep';
import { TemplateBuilderMediaStep } from './TemplateBuilderMediaStep';
import { TemplateBuilderPreviewStep } from './TemplateBuilderPreviewStep';

type TemplateBuilderStep = 'basic' | 'itinerary' | 'budget' | 'media' | 'preview';

type SubmitResult = 'published' | 'submitted' | 'updated';

type TemplateBuilderProps = {
  visible: boolean;
  onClose: () => void;
  onSuccess?: (templateId: string) => void;
  sourceTripId?: string; // Optional: Pre-fill builder with trip data
  existingTemplate?: TripTemplate | null; // Optional: Edit existing template (admin only)
  /** Pre-fill builder for duplicate / new-from-copy (no template id). */
  seedDraft?: Partial<TemplateBuilderDraft> | null;
  isAdminMode?: boolean;
  /** Called when admin saves a draft (builder stays open). */
  onDraftSaved?: (templateId: string) => void;
};

type TemplateDraft = Omit<
  TripTemplate,
  'id' | 'createdAt' | 'updatedAt' | 'usageCount' | 'status' | 'creatorId' | 'creatorName' | 'creatorAvatar' | 'createdBy' | 'featured'
>;

const INITIAL_DRAFT: Partial<TemplateDraft> = {
  title: '',
  destination: '',
  duration: 7,
  shortDescription: '',
  longDescription: '',
  difficulty: 'moderate',
  travelStyle: [],
  estimatedBudget: {
    min: 0,
    max: 0,
    currency: 'USD',
    breakdown: {
      flights: 0,
      lodging: 0,
      food: 0,
      activities: 0,
      transport: 0,
      other: 0,
    },
  },
  itinerary: [],
  bestMonths: [],
  tags: [],
  highlights: [],
};

export const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  visible,
  onClose,
  onSuccess,
  sourceTripId,
  existingTemplate,
  seedDraft,
  isAdminMode = false,
  onDraftSaved,
}) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const profile = useUserStore((state) => state.profile);
  const { showToastWithOptions } = useToast();

  const [currentStep, setCurrentStep] = useState<TemplateBuilderStep>('basic');
  const [draft, setDraft] = useState<Partial<TemplateDraft>>(INITIAL_DRAFT);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null);
  const [submittedTemplateId, setSubmittedTemplateId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingTripData, setLoadingTripData] = useState(false);
  const [persistedTemplateId, setPersistedTemplateId] = useState<string | null>(null);

  const activeTemplateId = existingTemplate?.id ?? persistedTemplateId;

  // Load trip data or existing template when modal opens
  useEffect(() => {
    if (!visible) return;
    setCurrentStep('basic');
    setErrors({});
    setSubmitResult(null);
    setSubmittedTemplateId(null);
    setPersistedTemplateId(existingTemplate?.id ?? null);
    if (existingTemplate) {
      setDraft(templateToBuilderDraft(existingTemplate));
    } else if (seedDraft) {
      setDraft({ ...INITIAL_DRAFT, ...seedDraft });
    } else if (sourceTripId) {
      setDraft(INITIAL_DRAFT);
      loadTripData(sourceTripId);
    } else {
      setDraft(INITIAL_DRAFT);
    }
  }, [visible, sourceTripId, existingTemplate, seedDraft]);

  const loadTripData = useCallback(async (tripId: string) => {
    console.log('[TemplateBuilder] loadTripData called with tripId:', tripId);
    setLoadingTripData(true);
    try {
      // Import Firestore functions and trips service
      const { doc, getDoc, Timestamp } = await import('firebase/firestore');
      const { db } = await import('@/lib/firebase');
      const { fetchTripItineraryItems } = await import('@/services/itinerary');
      
      // Fetch trip directly from Firestore
      const tripRef = doc(db, 'trips', tripId);
      const tripSnapshot = await getDoc(tripRef);
      
      if (!tripSnapshot.exists()) {
        Alert.alert('Error', 'Trip not found');
        return;
      }
      
      // Convert Firestore trip data to Trip type
      const tripData = tripSnapshot.data();
      const startDate = tripData.startDate instanceof Timestamp 
        ? tripData.startDate.toDate() 
        : typeof tripData.startDate === 'string' 
          ? new Date(tripData.startDate) 
          : new Date();
      
      const endDate = tripData.endDate instanceof Timestamp 
        ? tripData.endDate.toDate() 
        : typeof tripData.endDate === 'string' 
          ? new Date(tripData.endDate) 
          : new Date();
      
      const trip = {
        id: tripSnapshot.id,
        name: tripData.title || tripData.name || '',
        destination: tripData.destination || '',
        startDate,
        endDate,
        budget: tripData.budget || 0,
        baseCurrency: tripData.baseCurrency || 'USD',
        landmarkImage: tripData.landmarkImage,
        landmarkImageCredit: tripData.landmarkImageCredit,
        landmarkImageCreditUrl: tripData.landmarkImageCreditUrl,
        owner: tripData.owner || '',
      };
      
      console.log('[TemplateBuilder] Trip loaded:', { id: trip.id, name: trip.name, destination: trip.destination });

      const itineraryItems = await fetchTripItineraryItems(tripId);
      
      // Calculate duration
      const duration = Math.ceil(
        (trip.endDate.getTime() - trip.startDate.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;

      // Group items by day
      const daysByDate = new Map<string, any[]>();
      itineraryItems.forEach((item) => {
        const itemDate = new Date(item.startDate);
        itemDate.setHours(0, 0, 0, 0);
        const dateKey = itemDate.toISOString().split('T')[0];
        if (!daysByDate.has(dateKey)) {
          daysByDate.set(dateKey, []);
        }
        daysByDate.get(dateKey)!.push(item);
      });

      // Convert to template days
      const templateDays: any[] = [];
      const sortedDates = Array.from(daysByDate.keys()).sort();
      
      sortedDates.forEach((dateKey, index) => {
        const items = daysByDate.get(dateKey)!.sort((a, b) => {
          const timeA = a.startTime || '00:00';
          const timeB = b.startTime || '00:00';
          return timeA.localeCompare(timeB);
        });

        const categoryMap: Record<string, string> = {
          hotel: 'lodging',
          flight: 'flight',
          activity: 'activity',
          restaurant: 'restaurant',
          other: 'other',
        };

        const activities = items.map((item) => ({
          time: item.startTime || '09:00',
          title: item.title,
          category: categoryMap[item.category] || 'activity',
          description: item.description,
          venueName: item.location,
          geocodeHint: item.location,
          // Legacy compatibility with older template readers.
          location: item.location,
          address: item.address,
          duration: item.endTime && item.startTime
            ? calculateDurationMinutes(item.startTime, item.endTime)
            : undefined,
        }));

        templateDays.push({
          day: index + 1,
          title: `Day ${index + 1}: ${items[0]?.title || 'Activities'}`,
          activities,
        });
      });

      // Ensure days are properly numbered starting from 1
      const numberedTemplateDays = templateDays.map((day, idx) => ({
        ...day,
        day: idx + 1,
        title: day.title || `Day ${idx + 1}`,
      }));

      console.log('[TemplateBuilder] Loaded trip data:', {
        tripName: trip.name,
        destination: trip.destination,
        duration,
        itineraryDays: numberedTemplateDays.length,
        itinerary: numberedTemplateDays.map(d => ({ day: d.day, title: d.title, activities: d.activities?.length || 0 })),
      });

      // Pre-fill draft with trip data
      setDraft({
        title: `${trip.name} - Template`,
        destination: trip.destination || '',
        duration,
        shortDescription: `A ${duration}-day trip to ${trip.destination || 'your destination'}.`,
        longDescription: `Template based on trip: ${trip.name}`,
        coverImage: trip.landmarkImage,
        coverImageCredit: trip.landmarkImageCredit || undefined,
        coverImageCreditUrl: trip.landmarkImageCreditUrl || undefined,
        difficulty: 'moderate',
        travelStyle: [],
        estimatedBudget: {
          min: trip.budget ? Math.round(trip.budget * 0.8) : 0,
          max: trip.budget ? Math.round(trip.budget * 1.2) : 0,
          currency: trip.baseCurrency || 'USD',
          breakdown: {
            flights: 0,
            lodging: 0,
            food: 0,
            activities: 0,
            transport: 0,
          },
        },
        itinerary: numberedTemplateDays,
        bestMonths: [],
        tags: [],
        highlights: [],
      });
    } catch (error) {
      console.error('[TemplateBuilder] Error loading trip data:', error);
      Alert.alert('Error', 'Failed to load trip data. You can still create a template manually.');
    } finally {
      setLoadingTripData(false);
    }
  }, []);

  const calculateDurationMinutes = (startTime: string, endTime: string): number => {
    const [startHours, startMins] = startTime.split(':').map(Number);
    const [endHours, endMins] = endTime.split(':').map(Number);
    const startTotal = startHours * 60 + startMins;
    const endTotal = endHours * 60 + endMins;
    return endTotal - startTotal;
  };

  const steps: Array<{ key: TemplateBuilderStep; label: string; icon: keyof typeof Ionicons.glyphMap }> = [
    { key: 'basic', label: 'Basic Info', icon: 'information-circle-outline' },
    { key: 'itinerary', label: 'Itinerary', icon: 'calendar-outline' },
    { key: 'budget', label: 'Budget', icon: 'wallet-outline' },
    { key: 'media', label: 'Media', icon: 'image-outline' },
    { key: 'preview', label: 'Preview', icon: 'eye-outline' },
  ];

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);

  const handleNext = useCallback(() => {
    // Validate current step
    const stepErrors = validateStep(currentStep, draft);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    setErrors({});

    // Move to next step
    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1].key);
    }
  }, [currentStep, currentStepIndex, draft, steps]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].key);
      setErrors({});
    }
  }, [currentStepIndex, steps]);

  const handleDraftUpdate = useCallback((updates: Partial<TemplateDraft>) => {
    setDraft((prev) => ({ ...prev, ...updates }));
    // Clear errors when user makes changes
    setErrors({});
  }, []);

  const finishSubmit = useCallback(
    (templateId: string) => {
      onSuccess?.(templateId);
      onClose();
      setDraft(INITIAL_DRAFT);
      setCurrentStep('basic');
      setErrors({});
      setSubmitResult(null);
      setSubmittedTemplateId(null);
    },
    [onSuccess, onClose],
  );

  const handleSubmitSuccessDismiss = useCallback(() => {
    if (submittedTemplateId) {
      finishSubmit(submittedTemplateId);
    }
  }, [submittedTemplateId, finishSubmit]);

  const buildTemplateData = useCallback((): TemplateDraft => {
    return {
      ...draft,
      title: draft.title || '',
      destination: draft.destination || '',
      duration: draft.duration || 7,
      shortDescription: draft.shortDescription || '',
      difficulty: draft.difficulty || 'moderate',
      travelStyle: draft.travelStyle || [],
      estimatedBudget: draft.estimatedBudget || {
        min: 0,
        max: 0,
        currency: 'USD',
        breakdown: { flights: 0, lodging: 0, food: 0, activities: 0, transport: 0, other: 0 },
      },
      itinerary: draft.itinerary || [],
      bestMonths: draft.bestMonths || [],
      tags: draft.tags || [],
      highlights: draft.highlights || [],
    } as TemplateDraft;
  }, [draft]);

  const validateForDraft = useCallback((data: Partial<TemplateDraft>): Record<string, string> => {
    const stepErrors: Record<string, string> = {};
    if (!data.title || data.title.trim().length < 3) {
      stepErrors.title = 'Add a title (at least 3 characters) to save a draft';
    }
    return stepErrors;
  }, []);

  const runAdminSave = useCallback(
    async (publish: boolean) => {
      if (!firebaseUser?.uid) {
        Alert.alert('Error', 'You must be logged in to save a template.');
        return;
      }

      const validationErrors = publish ? validateAllSteps(draft) : validateForDraft(draft);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        const errorMessages = Object.values(validationErrors).filter(Boolean);
        Alert.alert(
          publish ? 'Validation Error' : 'Cannot save draft',
          `${errorMessages.slice(0, 5).join('\n')}${errorMessages.length > 5 ? `\n\n...and ${errorMessages.length - 5} more` : ''}`,
          [{ text: 'OK' }],
        );
        return;
      }

      setIsSubmitting(true);
      try {
        const templateData = buildTemplateData();
        let templateId: string;
        if (activeTemplateId) {
          templateId = await adminUpdateTripTemplate(activeTemplateId, templateData, { publish });
        } else {
          templateId = await adminCreateTripTemplate(templateData, { publish });
        }

        setPersistedTemplateId(templateId);

        if (publish) {
          const wasLiveBeforeSave =
            existingTemplate?.status !== 'draft' && !!existingTemplate?.id;
          const result: SubmitResult = wasLiveBeforeSave ? 'updated' : 'published';
          setSubmittedTemplateId(templateId);
          setSubmitResult(result);
          showToastWithOptions({
            message:
              result === 'updated'
                ? 'Template updated successfully.'
                : 'Template published — it is live in Trip Templates.',
            type: 'success',
            duration: 5000,
          });
        } else {
          onDraftSaved?.(templateId);
          showToastWithOptions({
            message: 'Draft saved. You can publish when ready.',
            type: 'success',
            duration: 4000,
          });
        }
      } catch (error) {
        console.error('[TemplateBuilder] Error saving template:', error);
        Alert.alert(
          'Error',
          error instanceof Error ? error.message : 'Failed to save template. Please try again.',
          [{ text: 'OK' }],
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      activeTemplateId,
      buildTemplateData,
      draft,
      firebaseUser,
      onDraftSaved,
      showToastWithOptions,
      validateForDraft,
    ],
  );

  const handleSaveDraft = useCallback(() => {
    if (!isAdminMode) return;
    void runAdminSave(false);
  }, [isAdminMode, runAdminSave]);

  const handleSubmit = useCallback(async () => {
    if (!firebaseUser?.uid) {
      Alert.alert('Error', 'You must be logged in to create a template.');
      return;
    }

    if (isAdminMode) {
      await runAdminSave(true);
      return;
    }

    const finalErrors = validateAllSteps(draft);
    if (Object.keys(finalErrors).length > 0) {
      setErrors(finalErrors);
      const errorMessages = Object.values(finalErrors).filter(Boolean);
      Alert.alert(
        'Validation Error',
        `Please fix the following errors:\n\n${errorMessages.slice(0, 5).join('\n')}${errorMessages.length > 5 ? `\n\n...and ${errorMessages.length - 5} more` : ''}`,
        [{ text: 'OK' }],
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const templateData = buildTemplateData();
      const templateId = await createUserTemplate(
        firebaseUser.uid,
        profile.name?.trim() || firebaseUser.email || 'Anonymous',
        profile.avatar?.type === 'upload' ? profile.avatar.uri : undefined,
        { ...templateData, featured: false },
      );

      setSubmittedTemplateId(templateId);
      setSubmitResult('submitted');
      showToastWithOptions({
        message: 'Template submitted for review. We will notify you when it is approved.',
        type: 'success',
        duration: 5000,
      });
    } catch (error) {
      console.error('[TemplateBuilder] Error submitting template:', error);
      Alert.alert(
        'Error',
        error instanceof Error ? error.message : 'Failed to create template. Please try again.',
        [{ text: 'OK' }],
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [buildTemplateData, draft, firebaseUser, isAdminMode, profile, runAdminSave, showToastWithOptions]);

  const validateStep = (step: TemplateBuilderStep, data: Partial<TemplateDraft>): Record<string, string> => {
    const stepErrors: Record<string, string> = {};

    switch (step) {
      case 'basic':
        if (!data.title || data.title.trim().length < 5) {
          stepErrors.title = 'Title must be at least 5 characters';
        }
        if (!data.destination || data.destination.trim().length < 3) {
          stepErrors.destination = 'Destination is required';
        }
        if (!data.shortDescription || data.shortDescription.trim().length < 50) {
          stepErrors.shortDescription = 'Short description must be at least 50 characters';
        }
        if (!data.duration || data.duration < 1 || data.duration > 365) {
          stepErrors.duration = 'Duration must be between 1 and 365 days';
        }
        if (!data.travelStyle || data.travelStyle.length === 0) {
          stepErrors.travelStyle = 'Select at least one travel style';
        }
        break;

      case 'itinerary':
        if (!data.itinerary || data.itinerary.length < MIN_TEMPLATE_ITINERARY_DAYS) {
          stepErrors.itinerary =
            MIN_TEMPLATE_ITINERARY_DAYS === 1
              ? 'Template must have at least 1 day of itinerary'
              : `Template must have at least ${MIN_TEMPLATE_ITINERARY_DAYS} days of itinerary`;
        } else {
          // Validate each day has activities
          data.itinerary.forEach((day, dayIndex) => {
            if (!day.title || day.title.trim().length === 0) {
              stepErrors[`itinerary_day_${dayIndex}_title`] = `Day ${dayIndex + 1} must have a title`;
            }
            if (!day.activities || day.activities.length === 0) {
              stepErrors[`itinerary_day_${dayIndex}_activities`] = `Day ${dayIndex + 1} must have at least one activity`;
            } else {
              // Validate each activity
              day.activities.forEach((activity, activityIndex) => {
                if (!activity.title || activity.title.trim().length === 0) {
                  stepErrors[`itinerary_day_${dayIndex}_activity_${activityIndex}_title`] = `Day ${dayIndex + 1}, Activity ${activityIndex + 1} must have a title`;
                }
                if (!activity.time || !/^\d{2}:\d{2}$/.test(activity.time)) {
                  stepErrors[`itinerary_day_${dayIndex}_activity_${activityIndex}_time`] = `Day ${dayIndex + 1}, Activity ${activityIndex + 1} must have a valid time (HH:mm)`;
                }
              });
            }
          });
        }
        break;

      case 'budget':
        if (!data.estimatedBudget) {
          stepErrors.budget = 'Budget is required';
        } else if (data.estimatedBudget.min < 0 || data.estimatedBudget.max < 0) {
          stepErrors.budget = 'Budget values must be positive';
        } else if (data.estimatedBudget.min > data.estimatedBudget.max) {
          stepErrors.budget = 'Minimum budget cannot be greater than maximum budget';
        }
        break;

      case 'media':
        // Media is optional, no validation needed
        break;

      case 'preview':
        // Final validation happens in handleSubmit
        break;
    }

    return stepErrors;
  };

  const validateAllSteps = (data: Partial<TemplateDraft>): Record<string, string> => {
    const allErrors: Record<string, string> = {};

    // Validate all steps
    (['basic', 'itinerary', 'budget'] as TemplateBuilderStep[]).forEach((step) => {
      const stepErrors = validateStep(step, data);
      Object.assign(allErrors, stepErrors);
    });

    return allErrors;
  };

  if (!visible) {
    console.log('[TemplateBuilder] Not visible, returning null');
    return null;
  }

  const submitResultCopy =
    submitResult === 'updated'
      ? {
          title: 'Template updated',
          body: 'Your changes have been saved. The live template reflects your latest updates.',
          icon: 'checkmark-circle' as const,
        }
      : submitResult === 'published'
        ? {
            title: 'Template published',
            body: 'Your template is live in Trip Templates. Travelers can browse and use it right away.',
            icon: 'rocket-outline' as const,
          }
        : submitResult === 'submitted'
          ? {
              title: 'Submitted for review',
              body: 'Thanks for sharing your trip. Our team will review it and notify you when it goes live.',
              icon: 'hourglass-outline' as const,
            }
          : null;

  console.log('[TemplateBuilder] Rendering, currentStep:', currentStep, 'currentStepIndex:', currentStepIndex, 'isSubmitting:', isSubmitting);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onClose}
    >
      <SelectFieldModalProvider>
      <View style={[styles.container, { flex: 1, backgroundColor: theme.colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { 
        backgroundColor: theme.colors.surface, 
        borderBottomColor: theme.colors.borderMuted,
        paddingTop: Math.max(insets.top, 16),
      }]}>
        <View style={styles.headerContent}>
          <Pressable onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color={theme.colors.text} />
          </Pressable>
          <View style={styles.headerTitle}>
            <ThemedText style={styles.headerTitleText}>
              {isAdminMode
                ? existingTemplate?.status === 'draft' || (!existingTemplate && persistedTemplateId)
                  ? 'Edit Draft'
                  : existingTemplate
                    ? 'Edit Template'
                    : seedDraft
                      ? 'Duplicate Template'
                      : 'Publish Template'
                : 'Create Template'}
            </ThemedText>
            <Caption color={theme.colors.textSecondary}>
              {submitResultCopy ? 'Complete' : `Step ${currentStepIndex + 1} of ${steps.length}`}
            </Caption>
          </View>
        </View>

        {/* Progress Steps */}
        {!submitResultCopy && (
        <View style={styles.progressContainer}>
          {steps.map((step, index) => (
            <View key={step.key} style={styles.progressStep}>
              <View
                style={[
                  styles.progressCircle,
                  {
                    backgroundColor:
                      index < currentStepIndex
                        ? theme.colors.primary
                        : index === currentStepIndex
                        ? theme.colors.primary
                        : theme.colors.borderMuted,
                  },
                ]}
              >
                <Ionicons
                  name={step.icon}
                  size={16}
                  color={index <= currentStepIndex ? theme.colors.surface : theme.colors.textSecondary}
                />
              </View>
              {index < steps.length - 1 && (
                <View
                  style={[
                    styles.progressLine,
                    {
                      backgroundColor:
                        index < currentStepIndex ? theme.colors.primary : theme.colors.borderMuted,
                    },
                  ]}
                />
              )}
            </View>
          ))}
        </View>
        )}
      </View>

      {/* Step Content */}
      {submitResultCopy ? (
        <View style={[styles.content, styles.successContent]}>
          <View style={[styles.successIconWrap, { backgroundColor: `${theme.colors.success}18` }]}>
            <Ionicons name={submitResultCopy.icon} size={48} color={theme.colors.success} />
          </View>
          <SubHeading style={styles.successTitle}>{submitResultCopy.title}</SubHeading>
          <Body color={theme.colors.textSecondary} style={styles.successBody}>
            {submitResultCopy.body}
          </Body>
          {draft.title ? (
            <Card variant="outlined" style={styles.successCard}>
              <Caption color={theme.colors.textSecondary}>Template</Caption>
              <Body style={{ fontWeight: '600', marginTop: 4 }}>{draft.title}</Body>
              {draft.destination ? (
                <Caption color={theme.colors.textSecondary} style={{ marginTop: 4 }}>
                  {draft.destination}
                </Caption>
              ) : null}
            </Card>
          ) : null}
        </View>
      ) : loadingTripData ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Body color={theme.colors.textSecondary} style={styles.loadingText}>
            Loading trip data...
          </Body>
        </View>
      ) : (
        <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
          {currentStep === 'basic' && (
            <TemplateBuilderBasicStep
              draft={draft}
              onUpdate={handleDraftUpdate}
              errors={errors}
            />
          )}

        {currentStep === 'itinerary' && (
          <TemplateBuilderItineraryStep
            draft={draft}
            onUpdate={handleDraftUpdate}
            errors={errors}
          />
        )}

        {currentStep === 'budget' && (
          <TemplateBuilderBudgetStep
            draft={draft}
            onUpdate={handleDraftUpdate}
            errors={errors}
          />
        )}

        {currentStep === 'media' && (
          <TemplateBuilderMediaStep
            draft={draft}
            onUpdate={handleDraftUpdate}
          />
        )}

        {currentStep === 'preview' && (
          <TemplateBuilderPreviewStep draft={draft} />
        )}
        </ScrollView>
      )}

      {/* Footer Actions */}
      <View style={[styles.footer, { 
        backgroundColor: theme.colors.surface, 
        borderTopColor: theme.colors.borderMuted,
        paddingBottom: Math.max(insets.bottom, 16),
      }]}>
        <View style={styles.footerActions}>
          {submitResultCopy ? (
            <Button
              label="Done"
              variant="primary"
              icon={<Ionicons name="checkmark" size={20} color={theme.colors.onPrimary || '#ffffff'} />}
              onPress={handleSubmitSuccessDismiss}
              style={[styles.footerButton, { flex: 1 }]}
            />
          ) : (
            <>
              {currentStepIndex > 0 && (
                <Button
                  label="Back"
                  variant="secondary"
                  icon={<Ionicons name="chevron-back" size={20} color={theme.colors.text} />}
                  onPress={handleBack}
                  style={styles.footerButton}
                />
              )}
              <View style={styles.footerSpacer} />
              {isAdminMode && (
                <Button
                  label={isSubmitting ? 'Saving…' : 'Save draft'}
                  variant="secondary"
                  icon={<Ionicons name="document-outline" size={18} color={theme.colors.text} />}
                  onPress={handleSaveDraft}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  style={styles.footerButton}
                />
              )}
              {currentStepIndex < steps.length - 1 ? (
                <Button
                  label="Next"
                  variant="primary"
                  icon={<Ionicons name="chevron-forward" size={20} color={theme.colors.onPrimary || '#ffffff'} />}
                  iconPosition="right"
                  onPress={handleNext}
                  style={styles.footerButton}
                />
              ) : (
                <Button
                  label={
                    isSubmitting
                      ? 'Submitting...'
                      : isAdminMode
                        ? existingTemplate?.status === 'draft' || persistedTemplateId
                          ? 'Publish'
                          : existingTemplate?.id
                            ? 'Save & publish'
                            : 'Publish Template'
                        : 'Submit for Review'
                  }
                  variant="primary"
                  icon={<Ionicons name="checkmark-circle" size={20} color={theme.colors.onPrimary || '#ffffff'} />}
                  iconPosition="right"
                  onPress={(e) => {
                    e?.stopPropagation?.();
                    handleSubmit();
                  }}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  style={styles.footerButton}
                />
              )}
            </>
          )}
        </View>
      </View>
    </View>
      </SelectFieldModalProvider>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingTop: Platform.OS === 'web' ? 16 : 50,
    paddingBottom: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  closeButton: {
    minWidth: 44,
    minHeight: 44,
    padding: 10,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    flex: 1,
  },
  headerTitleText: {
    fontSize: 20,
    fontWeight: '600',
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
  },
  progressStep: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  progressCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressLine: {
    flex: 1,
    height: 2,
    marginHorizontal: 4,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
  },
  footerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  footerButton: {
    flex: 1,
  },
  footerSpacer: {
    width: 12,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    marginTop: 16,
  },
  successContent: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  successIconWrap: {
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  successTitle: {
    textAlign: 'center',
    marginBottom: 8,
  },
  successBody: {
    textAlign: 'center',
    marginBottom: 20,
  },
  successCard: {
    width: '100%',
    maxWidth: 420,
  },
});

